/**
 * ChecklistDescriptionField
 *
 * Sticky note-styled task card with checklist items, color picker,
 * and optional instant-save on toggle (for edit mode).
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { InteractionManager, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import {
  EventDescriptionParser,
  type ChecklistItem,
} from '@/lib/utils/EventDescriptionParser';
import { ChecklistItemRow } from './ChecklistItemRow';
import { StickyNoteColorPicker, DEFAULT_TASK_NOTE_COLOR } from './StickyNoteColorPicker';

// Dark mode display equivalents for pastel task note colors
const DARK_MODE_CARD_COLORS: Record<string, string> = {
  '#FFFFFF': '', // white → uses palette.cardBackground (handled separately)
  '#FFF9C4': '#4A4535', // yellow → muted dark yellow
  '#FFCCBC': '#4A3D35', // peach → muted dark peach
  '#E1BEE7': '#3D3545', // lavender → muted dark lavender
  '#C8E6C9': '#354538', // mint → muted dark mint
  '#C5CAE9': '#353D4A', // periwinkle → muted dark periwinkle
};

type ChecklistDescriptionFieldProps = {
  value: string;
  onChange: (value: string) => void;
  palette: typeof Colors.light;
  paletteTextMuted: string;
  taskColor?: string | null;
  onTaskColorChanged?: (color: string) => void;
  onTaskToggled?: (serializedDescription: string) => void;
};

/** Darken a hex color by mixing with black */
function darkenColor(hex: string, amount: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const dr = Math.round(r * (1 - amount));
  const dg = Math.round(g * (1 - amount));
  const db = Math.round(b * (1 - amount));
  return `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
}

// Sticky note text colors based on background
const STICKY_TEXT_COLOR_DARK = '#333333';
const STICKY_CHECKBOX_COLOR_DARK = '#666666';
const STICKY_TEXT_COLOR_LIGHT = '#FFFFFF';
const STICKY_CHECKBOX_COLOR_LIGHT = '#B0B0B0';

export function ChecklistDescriptionField({
  value,
  onChange,
  palette,
  paletteTextMuted,
  taskColor,
  onTaskColorChanged,
  onTaskToggled,
}: ChecklistDescriptionFieldProps) {
  const colorScheme = useColorScheme() ?? 'light';

  const [textInput, setTextInput] = useState('');
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const itemRefs = useRef<Map<string, TextInput | null>>(new Map());
  const isInternalUpdate = useRef(false);
  const shouldSyncToParent = useRef(false);

  // Use refs for onTaskToggled and textInput to avoid breaking ChecklistItemRow memoization
  const onTaskToggledRef = useRef(onTaskToggled);
  onTaskToggledRef.current = onTaskToggled;
  const textInputRef = useRef(textInput);
  textInputRef.current = textInput;

  // Handle pastel cards in dark mode - use muted dark equivalents
  const rawColor = taskColor ?? DEFAULT_TASK_NOTE_COLOR;
  const isWhiteInDarkMode = colorScheme === 'dark' && rawColor === '#FFFFFF';
  const hasDarkModeEquivalent = colorScheme === 'dark' && rawColor in DARK_MODE_CARD_COLORS;

  // In dark mode, use dark equivalents for all pastel colors
  let activeColor: string;
  if (isWhiteInDarkMode) {
    activeColor = Colors[colorScheme].cardBackground;
  } else if (hasDarkModeEquivalent && DARK_MODE_CARD_COLORS[rawColor]) {
    activeColor = DARK_MODE_CARD_COLORS[rawColor];
  } else {
    activeColor = rawColor;
  }

  const borderColor = colorScheme === 'dark' ? palette.border : darkenColor(activeColor, 0.15);

  // Dynamic text colors based on dark mode (all dark mode cards need light text)
  const isDarkModeCard = colorScheme === 'dark';
  const stickyTextColor = isDarkModeCard ? STICKY_TEXT_COLOR_LIGHT : STICKY_TEXT_COLOR_DARK;
  const stickyCheckboxColor = isDarkModeCard ? STICKY_CHECKBOX_COLOR_LIGHT : STICKY_CHECKBOX_COLOR_DARK;

  // Parse external value changes into local state
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const parsed = EventDescriptionParser.parse(value);
    setTextInput(parsed.textLines.join('\n'));
    setItems(parsed.checklistItems);
  }, [value]);

  // Sync local state changes back to parent
  useEffect(() => {
    if (!shouldSyncToParent.current) {
      return;
    }

    const textLines = textInput.trim() ? textInput.split('\n') : [];
    const serialized = EventDescriptionParser.serialize(textLines, items);
    isInternalUpdate.current = true;
    onChange(serialized);
    shouldSyncToParent.current = false;
  }, [textInput, items, onChange]);

  const handleToggleItem = useCallback((itemId: string) => {
    shouldSyncToParent.current = true;
    setItems(prevItems => {
      const newItems = prevItems.map(item =>
        item.id === itemId ? { ...item, completed: !item.completed } : item
      );

      // Fire instant save if callback is provided
      if (onTaskToggledRef.current) {
        const textLines = textInputRef.current.trim()
          ? textInputRef.current.split('\n')
          : [];
        const serialized = EventDescriptionParser.serialize(textLines, newItems);
        onTaskToggledRef.current(serialized);
      }

      return newItems;
    });
  }, []);

  const handleUpdateItemText = useCallback((itemId: string, newText: string) => {
    shouldSyncToParent.current = true;
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, text: newText } : item
      )
    );
  }, []);

  const handleAddItem = useCallback(() => {
    shouldSyncToParent.current = true;
    const newItem: ChecklistItem = {
      id: `task_temp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      text: '',
      completed: false,
      lineIndex: -1,
    };
    setItems(prevItems => [...prevItems, newItem]);

    InteractionManager.runAfterInteractions(() => {
      setTimeout(() => {
        const ref = itemRefs.current.get(newItem.id);
        if (ref) {
          ref.focus();
        }
      }, 50);
    });
  }, []);

  const handleInsertAfter = useCallback((itemId: string, trailingText: string) => {
    shouldSyncToParent.current = true;
    const newItem: ChecklistItem = {
      id: `task_temp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      text: trailingText,
      completed: false,
      lineIndex: -1,
    };
    setItems(prevItems => {
      const index = prevItems.findIndex(i => i.id === itemId);
      const updated = [...prevItems];
      updated.splice(index + 1, 0, newItem);
      return updated;
    });

    InteractionManager.runAfterInteractions(() => {
      setTimeout(() => {
        const ref = itemRefs.current.get(newItem.id);
        if (ref) {
          ref.focus();
        }
      }, 50);
    });
  }, []);

  const handleRemoveItem = useCallback((itemId: string) => {
    shouldSyncToParent.current = true;
    itemRefs.current.delete(itemId);
    setItems(prevItems => prevItems.filter(item => item.id !== itemId));
  }, []);

  const handleItemRef = useCallback((id: string, ref: TextInput | null) => {
    if (ref) {
      itemRefs.current.set(id, ref);
    } else {
      itemRefs.current.delete(id);
    }
  }, []);

  const hasItems = items.length > 0;

  return (
    <View style={styles.section}>
      <View
        style={[
          styles.card,
          {
            backgroundColor: activeColor,
            borderWidth: 1,
            borderColor: borderColor,
          },
        ]}
      >
        {/* Header with title and color picker */}
        <View style={[styles.header, { borderBottomColor: borderColor }, !hasItems && styles.headerNoTasks]}>
          <Text style={[styles.title, { color: stickyTextColor, fontFamily: getBodyFont() }]}>
            Tasks
          </Text>
          {onTaskColorChanged && (
            <StickyNoteColorPicker
              selectedColor={rawColor}
              onColorSelected={onTaskColorChanged}
              iconColor={stickyCheckboxColor}
            />
          )}
        </View>

        {/* Task items */}
        {hasItems && (
          <View style={styles.tasksList}>
            {items.map((item, index) => (
              <View
                key={item.id}
                style={[
                  styles.taskItemWrapper,
                  index < items.length - 1 && { borderBottomColor: borderColor, borderBottomWidth: 1 },
                ]}
              >
                <ChecklistItemRow
                  item={item}
                  onToggle={handleToggleItem}
                  onUpdateText={handleUpdateItemText}
                  onRemove={handleRemoveItem}
                  onInsertAfter={handleInsertAfter}
                  palette={palette}
                  paletteTextMuted={paletteTextMuted}
                  checkboxColor={stickyCheckboxColor}
                  textColor={stickyTextColor}
                  itemRef={handleItemRef}
                />
              </View>
            ))}
          </View>
        )}

        {/* Add task button */}
        <TouchableOpacity onPress={handleAddItem} style={styles.addButton}>
          <Ionicons name="add-circle-outline" size={18} color={stickyCheckboxColor} />
          <Text style={[styles.addButtonText, { color: stickyTextColor, fontFamily: getBodyFont() }]}>
            Add task
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    gap: Spacing.md,
  },
  card: {
    padding: Spacing.lg,
    borderRadius: BorderRadius.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: Spacing.md,
    borderBottomWidth: 1,
  },
  headerNoTasks: {
    borderBottomWidth: 0,
    paddingBottom: Spacing.none,
  },
  title: {
    fontSize: 14,
    fontWeight: '700',
  },
  tasksList: {
    marginTop: Spacing.md,
  },
  taskItemWrapper: {
    // Wrapper for border separators
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    paddingVertical: Spacing.sm,
    marginTop: Spacing.sm,
  },
  addButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
